import React, { ReactNode } from "react";
import * as Ons from "react-onsenui";
import { Page } from "../../activities/AppPage";
import { isLoading, Loading } from "../../Loading";
import { CoronaReportAllQuestionsResponse } from "../../messages";
import { BELL_ICON, VIRUS_ICON } from "../../utils/svgs_new";
import { AppIcon } from "../Generic/AppIcon";
import AppModal from "../Generic/AppModal/AppModal";
import IconWithBadge from "../IconWithBadge/IconWithBadge";
import OpenInApp from "../OpenInApp";
import { PendingPatientTasksBottomSheet } from "../PendingPatientTasksBottomSheet/PendingPatientTasksBottomSheet";
import ScaffoldLogo from "../Scaffold/components/ScaffoldLogo";
import Scaffold from "../Scaffold/Scaffold";
import useModal from "../useModal";
import { MainScreenTab } from "./MainTypes";
import ScaffoldNavFooter from "./ScaffoldNavFooter";

interface Props {
  activeScreenTab: MainScreenTab;
  visitsView: ReactNode;
  trainingCenterView: ReactNode;
  patientsView: ReactNode;
  chatView: ReactNode;
  extraHeader: ReactNode;
  sidebar: ReactNode;
  covidResponse: CoronaReportAllQuestionsResponse | Loading;
  totalNewVideos: number;
  totalNewChats: number;
  totalNewNotifications: number;
  isMenuOpen: boolean;
  pendingTasksAmount: number;
  isOpenPendingPatientTasksBottomSheet: boolean;
  isCaregiverOfMedFlyt: boolean;
  isInstapayVisible: boolean;
  startActions: ReactNode;
  children?: React.ReactNode;
  onRequestClosePendingPatientTasksBottomSheet: () => void;
  onClickReviewTasks: () => void;
  onClickTab: (tab: MainScreenTab) => void;
  onClickNotifications: () => void;
  onClickCovidQuestionnaire: (report: CoronaReportAllQuestionsResponse) => void;
  onRequestNavigate: (page: Page) => void;
  onClickMenu: () => void;
  onCloseMenu: () => void;
}

const MainScreen = (props: Props) => {
  const instaPayModal = useModal();
  const onClickTab = props.onClickTab;
  const handleClickVisitsTab = React.useCallback(() => onClickTab("Visits"), [onClickTab]);
  const handleClickTrainingTab = React.useCallback(
    () => onClickTab("TrainingCenter"),
    [onClickTab]
  );
  const handleClickPatientsTab = React.useCallback(() => onClickTab("Patients"), [onClickTab]);
  const handleClickChatTab = React.useCallback(() => onClickTab("Chat"), [onClickTab]);

  const handleClickInstaPay = () => {
    return props.onRequestNavigate.call(null, { type: "InstaPay" });
  };

  const scaffoldNavFooter = (
    <ScaffoldNavFooter
      totalNewChats={props.totalNewChats}
      totalNewVideos={props.totalNewVideos}
      activeTab={props.activeScreenTab}
      isCaregiverOfMedflyt={props.isCaregiverOfMedFlyt}
      isInstapayVisible={props.isInstapayVisible}
      onClickVisits={handleClickVisitsTab}
      onClickTraining={handleClickTrainingTab}
      onClickInstaPay={handleClickInstaPay}
      onClickPatients={handleClickPatientsTab}
      onClickChat={handleClickChatTab}
    />
  );

  const notificationsIcon = React.useMemo(() => {
    return (
      <IconWithBadge
        icon={BELL_ICON}
        badge={props.totalNewNotifications}
        fill="#404040"
        onClick={props.onClickNotifications}
      />
    );
  }, [props.totalNewNotifications, props.onClickNotifications]);

  const covidIcon = React.useMemo(() => {
    const report = props.covidResponse;

    if (isLoading(report)) {
      return <Ons.ProgressCircular style={{ height: 24 }} />;
    }

    if (report.caregiverQuestions === null && report.patientQuestions === null) {
      return null;
    }

    return (
      <div className="__covidIcon">
        <AppIcon
          icon={VIRUS_ICON}
          onClick={() => props.onClickCovidQuestionnaire.call(null, report)}
        />
        <div className="__shadow" />
      </div>
    );
  }, [props.covidResponse, props.onClickCovidQuestionnaire]);

  const scaffoldActions = React.useMemo(() => {
    return props.isCaregiverOfMedFlyt ? (
      notificationsIcon
    ) : (
      <>
        {covidIcon}
        {notificationsIcon}
      </>
    );
  }, [covidIcon, notificationsIcon, props.isCaregiverOfMedFlyt]);

  const scaffold = (
    <Scaffold
      title={<ScaffoldLogo />}
      extraTop={<OpenInApp />}
      startActions={props.startActions}
      extraHeader={props.extraHeader}
      className="MainScreen"
      endActions={scaffoldActions}
      footer={scaffoldNavFooter}
      onClickMenu={props.onClickMenu}
    >
      <div style={{ display: props.activeScreenTab === "Visits" ? "block" : "none" }}>
        {props.visitsView}
      </div>
      <div
        style={{
          display: props.activeScreenTab === "TrainingCenter" ? "block" : "none",
        }}
      >
        {props.trainingCenterView}
      </div>
      <div style={{ display: props.activeScreenTab === "Patients" ? "block" : "none" }}>
        {props.patientsView}
      </div>
      <div style={{ display: props.activeScreenTab === "Chat" ? "block" : "none" }}>
        {props.chatView}
      </div>
      <PendingPatientTasksBottomSheet
        isOpen={props.isOpenPendingPatientTasksBottomSheet}
        pendingTasksAmount={props.pendingTasksAmount}
        onClickReviewTasks={props.onClickReviewTasks}
        onRequestClose={props.onRequestClosePendingPatientTasksBottomSheet}
      />
      {props.children}
      <AppModal {...instaPayModal.modalProps} />
    </Scaffold>
  );

  return (
    <Ons.Splitter>
      <Ons.SplitterSide
        side="left"
        width={300}
        collapse={true}
        swipeable={true}
        swipeTargetWidth={1}
        isOpen={props.isMenuOpen}
        onClose={props.onCloseMenu}
      >
        <Ons.Page>{props.sidebar}</Ons.Page>
      </Ons.SplitterSide>
      <Ons.SplitterContent>
        <Ons.Page>{scaffold}</Ons.Page>
      </Ons.SplitterContent>
    </Ons.Splitter>
  );
};

export default React.memo(MainScreen);
